if (!window.FCT) {
  window.FCT = {};
}

if (!window.FCT.pdpBundle) {
  window.FCT.pdpBundle = (function createPdpBundleManager() {
    const MODE_PARAM = 'pdp_bundle';
    const STORAGE_KEY = 'fct_pdp_bundle_v1';

    const state = {
      listenersBound: false,
      addingToCart: false,
      memoryState: {
        items: []
      }
    };

    const escapeHtml = (value) => String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const readUrl = () => {
      try {
        return new URL(window.location.href);
      } catch (error) {
        return null;
      }
    };

    const parseBoolean = (value, fallback = false) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
      }
      return fallback;
    };

    const parsePositiveNumber = (value, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    };

    const parseCsvParams = (value) => String(value || '')
      .split(',')
      .map((param) => param.trim())
      .filter((param, index, list) => {
        if (!param) return false;
        if (param === MODE_PARAM) return false;
        return list.indexOf(param) === index;
      });

    const readRuntimeSettings = () => {
      const themeSettings = window.theme?.settings || {};
      const runtimeSettingsEl = document.querySelector('[data-pdp-bundle-settings]');
      const barEl = document.querySelector('[data-pdp-bundle-bar]');

      const enabled = runtimeSettingsEl
        ? parseBoolean(runtimeSettingsEl.dataset.enabled, Boolean(themeSettings.pdp_bundle_builder_enable))
        : Boolean(themeSettings.pdp_bundle_builder_enable);
      const limit = runtimeSettingsEl
        ? parsePositiveNumber(runtimeSettingsEl.dataset.limit, parsePositiveNumber(themeSettings.pdp_bundle_builder_limit, 5))
        : parsePositiveNumber(themeSettings.pdp_bundle_builder_limit, 5);
      const discount = runtimeSettingsEl
        ? parsePositiveNumber(runtimeSettingsEl.dataset.discount, parsePositiveNumber(themeSettings.pdp_bundle_builder_discount, 0))
        : parsePositiveNumber(themeSettings.pdp_bundle_builder_discount, 0);
      const rawUrlParams = runtimeSettingsEl
        ? String(runtimeSettingsEl.dataset.urlParams || '')
        : String(themeSettings.pdp_bundle_builder_url_params || '');
      const swatchGuidanceBody = runtimeSettingsEl
        ? runtimeSettingsEl.dataset.swatchGuidanceBody
        : themeSettings.pdp_bundle_builder_swatch_guidance_body;
      const preAddBarBgColor = runtimeSettingsEl?.dataset.preAddBarBgColor
        || barEl?.dataset.preAddBarBgColor
        || themeSettings.pdp_bundle_builder_pre_add_bar_bg_color
        || '#ffffff';
      const addedBarBgColor = runtimeSettingsEl?.dataset.addedBarBgColor
        || barEl?.dataset.addedBarBgColor
        || themeSettings.pdp_bundle_builder_added_bar_bg_color
        || preAddBarBgColor;

      return {
        enabled,
        limit,
        discount,
        additionalModeParams: parseCsvParams(rawUrlParams),
        swatchGuidanceBody,
        preAddBarBgColor,
        addedBarBgColor
      };
    };

    const isFeatureEnabled = () => Boolean(readRuntimeSettings().enabled);

    const getLimit = () => {
      return parsePositiveNumber(readRuntimeSettings().limit, 5);
    };

    const getDiscountCents = () => {
      const discountDollars = parsePositiveNumber(readRuntimeSettings().discount, 0);
      return Math.round(discountDollars * 100);
    };

    const getSwatchGuidanceBody = () => {
      return readRuntimeSettings().swatchGuidanceBody;
    };

    const getAdditionalModeParams = () => {
      return readRuntimeSettings().additionalModeParams;
    };

    const hasModeParam = () => {
      const url = readUrl();
      if (!url) return false;

      if (url.searchParams.get(MODE_PARAM) === '1') {
        return true;
      }

      return getAdditionalModeParams().some((param) => url.searchParams.has(param));
    };

    const normalizeImage = (image) => {
      if (!image || typeof image !== 'object') return null;
      const src = String(image.src || '').trim();
      if (!src) return null;
      return {
        src,
        srcset: String(image.srcset || '').trim(),
        sizes: String(image.sizes || '').trim()
      };
    };

    const normalizeItem = (item) => {
      if (!item || typeof item !== 'object') return null;
      const handle = String(item.handle || '').trim();
      const variantId = Number(item.variantId || item.variant_id);
      if (!handle || !Number.isFinite(variantId) || variantId <= 0) return null;
      const price = Number(item.price);
      const compareAtPrice = Number(item.compareAtPrice || item.compare_at_price);
      const bundlePrice = Number(item.bundlePrice || item.bundle_price);

      return {
        handle,
        variantId,
        title: String(item.title || '').trim(),
        variantTitle: String(item.variantTitle || '').trim(),
        swatchLabel: String(item.swatchLabel || '').trim(),
        swatchValue: String(item.swatchValue || '').trim(),
        displayMode: String(item.displayMode || 'color').trim().toLowerCase(),
        image: normalizeImage(item.image),
        price: Number.isFinite(price) && price >= 0 ? price : 0,
        compareAtPrice: Number.isFinite(compareAtPrice) && compareAtPrice >= 0 ? compareAtPrice : 0,
        bundlePrice: Number.isFinite(bundlePrice) && bundlePrice >= 0 ? bundlePrice : Math.max(0, (Number.isFinite(price) && price >= 0 ? price : 0) - getDiscountCents()),
        addedAt: Number(item.addedAt || Date.now())
      };
    };

    const formatMoney = (value) => {
      const cents = Number(value || 0);
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.theme?.settings?.money_with_currency_format || '${{amount}}');
      }

      return `$${(cents / 100).toFixed(2)}`;
    };

    const getPriceTotals = (items) => {
      const normalizedItems = Array.isArray(items) ? items : [];

      return normalizedItems.reduce((totals, item) => {
        const price = Number(item?.price || 0);
        const safePrice = Number.isFinite(price) && price >= 0 ? price : 0;
        const bundlePrice = Number(item?.bundlePrice);
        const safeBundlePrice = Number.isFinite(bundlePrice) && bundlePrice >= 0 ? bundlePrice : Math.max(0, safePrice - getDiscountCents());

        totals.price += safeBundlePrice;
        totals.compareAtPrice += safePrice;

        return totals;
      }, { price: 0, compareAtPrice: 0 });
    };

    const getRemainingCopy = (remaining) => {
      const safeRemaining = Math.max(0, Number(remaining) || 0);
      const noun = safeRemaining === 1 ? 'ITEM' : 'ITEMS';
      return `ADD ${safeRemaining} MORE ${noun}`;
    };

    const getCurrentVariantPriceSnapshot = (priceEl) => {
      if (!priceEl) return null;

      const price = Number(priceEl.dataset.price || 0);
      const comparePrice = Number(priceEl.dataset.comparePrice || price);

      return {
        price: Number.isFinite(price) && price >= 0 ? price : 0,
        comparePrice: Number.isFinite(comparePrice) && comparePrice >= 0 ? comparePrice : (Number.isFinite(price) && price >= 0 ? price : 0)
      };
    };

    const renderProductPrices = () => {
      const priceEls = document.querySelectorAll('[data-pdp-bundle-price]');
      if (!priceEls.length) return;

      const active = isActive();
      const discountCents = getDiscountCents();

      priceEls.forEach((priceEl) => {
        priceEl.hidden = !active;
        if (!active) return;

        const snapshot = getCurrentVariantPriceSnapshot(priceEl);
        if (!snapshot) return;

        const compareEl = priceEl.querySelector('[data-pdp-bundle-price-compare]');
        const currentEl = priceEl.querySelector('[data-pdp-bundle-price-current]');
        const comparePrice = snapshot.comparePrice;
        const currentPrice = Math.max(0, snapshot.price - discountCents);

        if (compareEl) {
          compareEl.textContent = formatMoney(comparePrice);
          compareEl.hidden = false;
        }

        if (currentEl) {
          currentEl.textContent = formatMoney(currentPrice);
          currentEl.hidden = true;
        }
      });
    };

    const readStoredState = () => {
      try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return state.memoryState;
        const parsed = JSON.parse(raw);
        const items = Array.isArray(parsed?.items) ? parsed.items.map(normalizeItem).filter(Boolean) : [];
        state.memoryState = {
          items: items.slice(0, getLimit())
        };
        return state.memoryState;
      } catch (error) {
        return state.memoryState;
      }
    };

    const writeStoredState = (nextState) => {
      const items = Array.isArray(nextState?.items) ? nextState.items.map(normalizeItem).filter(Boolean) : [];
      const payload = {
        items: items.slice(0, getLimit())
      };
      state.memoryState = payload;

      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        // Keep an in-memory fallback when storage is unavailable.
      }

      return payload;
    };

    const getState = () => readStoredState();

    const setState = (nextState, detail = {}) => {
      const payload = writeStoredState(nextState);
      syncModeClass();
      renderBars();
      renderProductPrices();
      document.dispatchEvent(new CustomEvent('fct:pdp-bundle:update', {
        detail: {
          ...detail,
          state: payload
        }
      }));
      return payload;
    };

    const getItems = () => getState().items;

    const isActive = () => isFeatureEnabled() && hasModeParam();

    const getCountByHandle = (handle) => {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return 0;
      return getItems().reduce((total, item) => total + (item.handle === normalizedHandle ? 1 : 0), 0);
    };

    const isFull = () => getItems().length >= getLimit();

    const addModeParam = () => {
      const url = readUrl();
      if (!url) return;
      url.searchParams.set(MODE_PARAM, '1');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const removeModeParam = () => {
      const url = readUrl();
      if (!url) return;
      url.searchParams.delete(MODE_PARAM);
      const search = url.searchParams.toString();
      window.history.replaceState(window.history.state, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
    };

    const syncModeClass = () => {
      const active = isActive();
      document.documentElement.classList.toggle('pdp-bundle-mode-active', active);
      document.body.classList.toggle('pdp-bundle-mode-active', active);
    };

    const handleSwatchPattern = (color) => {
      const raw = String(color || '').trim();
      if (!raw) return '';
      if (!raw.includes('-')) return `background-color: ${raw};`;

      const parts = raw.split('-');
      const type = String(parts[0] || '').toLowerCase();

      if ((type === 'striped' || type === 'stripedbig') && parts.length >= 4) {
        const angle = parts[1] || '45deg';
        const color1 = parts[2] || '';
        const color2 = parts[3] || '';
        const stripeWidth = type === 'stripedbig' ? 6 : 3;
        return `background: repeating-linear-gradient(${angle}, ${color1}, ${color1} ${stripeWidth}px, ${color2} ${stripeWidth}px, ${color2} ${stripeWidth * 2}px); background-size: ${type === 'stripedbig' ? 10 : 5}px;`;
      }

      if (type === 'gingham' && parts.length >= 3) {
        const color1 = parts[1] || '';
        const color2 = parts[2] || '';
        return `background-image: repeating-linear-gradient(0deg, ${color1}80 0 4px, transparent 4px 8px), repeating-linear-gradient(90deg, ${color1}80 0 4px, transparent 4px 8px); background-color: ${color2};`;
      }

      return `background-color: ${raw};`;
    };

    const renderSwatchThumb = (item, extraClass = '') => {
      const className = `pdp-bundle-swatch${extraClass ? ` ${extraClass}` : ''}`;
      const label = escapeHtml(item.swatchLabel || item.title || item.handle);

      if (item.displayMode === 'image' && item.image?.src) {
        const src = escapeHtml(item.image.src);
        const srcset = item.image.srcset ? ` srcset="${escapeHtml(item.image.srcset)}"` : '';
        const sizes = item.image.sizes ? ` sizes="${escapeHtml(item.image.sizes)}"` : '';
        return `<span class="${className}" title="${label}"><img src="${src}"${srcset}${sizes} alt="${label}" loading="lazy" decoding="async"></span>`;
      }

      return `<span class="${className}" title="${label}" style="${escapeHtml(handleSwatchPattern(item.swatchValue))}"></span>`;
    };

    const renderAddedSwatch = (item, index) => {
      const safeIndex = Number(index);
      const itemLabel = escapeHtml(item.swatchLabel || item.title || item.handle);

      return `
        <span class="pdp-bundle-bar__added-item">
          ${renderSwatchThumb(item, 'pdp-bundle-bar__added-swatch')}
          <button
            type="button"
            class="pdp-bundle-bar__added-remove"
            data-pdp-bundle-remove-index="${safeIndex}"
            aria-label="Remove ${itemLabel}"
          >
            <span aria-hidden="true">x</span>
          </button>
        </span>
      `;
    };

    const renderBars = () => {
      const containers = document.querySelectorAll('[data-pdp-bundle-bar]');
      if (!containers.length) return;

      const active = isActive();
      const runtimeSettings = readRuntimeSettings();
      const limit = getLimit();
      const items = getItems();
      const count = items.length;
      const remaining = Math.max(0, limit - count);
      const totals = getPriceTotals(items);
      const totalBundleSavings = getDiscountCents() * limit;
      const progressMarkup = Array.from({ length: limit }, (_, index) => `<span class="pdp-bundle-bar__progress-pill${index < count ? ' is-filled' : ''}"></span>`).join('');
      const chipsMarkup = items.length
        ? items.map((item, index) => renderAddedSwatch(item, index)).join('')
        : '<span class="pdp-bundle-bar__empty">No colors added yet</span>';

      containers.forEach((container) => {
        container.style.setProperty('--pdp-bundle-pre-add-bar-bg-color', runtimeSettings.preAddBarBgColor || '#ffffff');
        container.style.setProperty('--pdp-bundle-added-bar-bg-color', runtimeSettings.addedBarBgColor || runtimeSettings.preAddBarBgColor || '#ffffff');
        container.hidden = !active;
        container.classList.toggle('has-items', active && count > 0);
        if (!active) return;

        const headingTextEl = container.querySelector('[data-pdp-bundle-heading-text]');
        const progressEl = container.querySelector('[data-pdp-bundle-progress]');
        const addedEl = container.querySelector('[data-pdp-bundle-added]');
        const buttonEl = container.querySelector('[data-pdp-bundle-cta]');
        const buttonLabelEl = container.querySelector('[data-pdp-bundle-cta-label]');
        const buttonPriceEl = container.querySelector('[data-pdp-bundle-cta-price]');
        const buttonNoteEl = container.querySelector('[data-pdp-bundle-cta-note]');

        if (headingTextEl) {
          headingTextEl.textContent = remaining > 0 ? getRemainingCopy(remaining) : `YOUR ${limit}-PACK ${count}/${limit}`;
        }

        if (progressEl) {
          progressEl.innerHTML = progressMarkup;
        }

        if (addedEl) {
          addedEl.innerHTML = chipsMarkup;
        }

        if (buttonEl) {
          const hasItems = count > 0;
          buttonEl.disabled = !hasItems || state.addingToCart;
          buttonEl.classList.toggle('is-ready', hasItems);
          buttonEl.classList.toggle('is-loading', state.addingToCart);
        }

        if (buttonLabelEl) {
          if (state.addingToCart) {
            buttonLabelEl.textContent = 'ADDING...';
          } else {
            buttonLabelEl.textContent = count > 0 ? 'ADD TO CART \u2014 ' : 'Select size, then select colors by clicking on them';
          }
        }

        if (buttonPriceEl) {
          if (count > 0) {
            if (count >= limit && totals.compareAtPrice > totals.price) {
              buttonPriceEl.innerHTML = `<span class="pdp-bundle-bar__price-compare">${formatMoney(totals.compareAtPrice)}</span><span class="pdp-bundle-bar__price-current">${formatMoney(totals.price)}</span>`;
            } else {
              buttonPriceEl.innerHTML = `<span class="pdp-bundle-bar__price-current">${formatMoney(totals.compareAtPrice)}</span>`;
            }
          } else {
            buttonPriceEl.innerHTML = '';
          }
        }

        if (buttonNoteEl) {
          if (count > 0 && count < limit) {
            buttonNoteEl.textContent = `(Add ${remaining} more ${remaining === 1 ? 'item' : 'items'} to save ${formatMoney(totalBundleSavings)})`;
          } else {
            buttonNoteEl.textContent = '';
          }
        }
      });
    };

    const getSectionsToRender = () => {
      return [
        {
          id: 'Cart',
          section: 'main-cart',
          selector: '.thb-cart-form'
        },
        {
          id: 'Cart-Drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer'
        },
        {
          id: 'cart-drawer-toggle',
          section: 'cart-bubble',
          selector: '.thb-item-count'
        }
      ];
    };

    const getSectionInnerHTML = (html, selector = '.shopify-section') => {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
    };

    const renderSections = (parsedState) => {
      const sections = parsedState?.sections;
      if (!sections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      getSectionsToRender().forEach((section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl || !sections[section.section]) return;

        const elementToReplace = sectionEl.querySelector(section.selector) || sectionEl;
        elementToReplace.innerHTML = getSectionInnerHTML(sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.notesToggle?.();
          document.getElementById('Cart-Drawer')?.removeProductEvent?.();
        }

        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      });
    };

    const buildCartItemsPayload = () => {
      const groupedItems = getItems().reduce((acc, item) => {
        const key = String(item.variantId);
        if (!acc[key]) {
          acc[key] = {
            id: item.variantId,
            quantity: 0
          };
        }

        acc[key].quantity += 1;
        return acc;
      }, {});

      return Object.values(groupedItems);
    };

    const addBundleToCart = async () => {
      if (state.addingToCart || !isActive()) return;

      const items = buildCartItemsPayload();
      if (!items.length) return;

      state.addingToCart = true;
      renderBars();

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          })
        });

        const parsedState = await response.json();

        if (!response.ok || parsedState?.status) {
          if (parsedState?.status === 422) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }

          throw new Error(parsedState?.description || parsedState?.message || 'Unable to add bundle to cart.');
        }

        renderSections(parsedState);

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: parsedState.hasOwnProperty('items') ? parsedState.items[0] : parsedState
          });
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: {
              product: parsedState?.items?.[0] || parsedState
            }
          }));
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        openCartDrawer();
        deactivate({ clearState: true });
      } catch (error) {
        console.error('PDP bundle add-to-cart failed', error);
      } finally {
        state.addingToCart = false;
        renderBars();
      }
    };

    const openCartDrawer = () => {
      const cartDrawerEl = document.getElementById('Cart-Drawer');
      if (!cartDrawerEl) return;

      document.body.classList.add('open-cc', 'open-cart');
      cartDrawerEl.classList.add('active');
      cartDrawerEl.focus?.();
      cartDrawerEl.querySelector('.product-recommendations--full')?.classList.add('active');
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart-drawer:open');
      }
    };

    const activate = (detail = {}) => {
      if (!isFeatureEnabled()) return false;
      addModeParam();
      syncModeClass();
      renderBars();
      renderProductPrices();
      document.dispatchEvent(new CustomEvent('fct:pdp-bundle:mode-change', {
        detail: {
          active: true,
          ...detail
        }
      }));
      return true;
    };

    const deactivate = ({ clearState = false } = {}) => {
      removeModeParam();
      if (clearState) {
        writeStoredState({ items: [] });
      }
      syncModeClass();
      renderBars();
      renderProductPrices();
      document.dispatchEvent(new CustomEvent('fct:pdp-bundle:mode-change', {
        detail: {
          active: false
        }
      }));
    };

    const addItem = (item, detail = {}) => {
      if (!isActive()) return { ok: false, reason: 'inactive' };
      const normalizedItem = normalizeItem(item);
      if (!normalizedItem) return { ok: false, reason: 'invalid-item' };

      const currentState = getState();
      if (currentState.items.length >= getLimit()) {
        return { ok: false, reason: 'full' };
      }

      currentState.items.push(normalizedItem);
      setState(currentState, { action: 'add', item: normalizedItem, ...detail });
      return { ok: true, item: normalizedItem };
    };

    const removeLastByHandle = (handle, detail = {}) => {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return { ok: false, reason: 'invalid-handle' };

      const currentState = getState();
      for (let index = currentState.items.length - 1; index >= 0; index -= 1) {
        if (currentState.items[index].handle !== normalizedHandle) continue;
        const [removed] = currentState.items.splice(index, 1);
        setState(currentState, { action: 'remove', item: removed, ...detail });
        return { ok: true, item: removed };
      }

      return { ok: false, reason: 'not-found' };
    };

    const removeItemAt = (index, detail = {}) => {
      const normalizedIndex = Number(index);
      if (!Number.isInteger(normalizedIndex) || normalizedIndex < 0) {
        return { ok: false, reason: 'invalid-index' };
      }

      const currentState = getState();
      if (normalizedIndex >= currentState.items.length) {
        return { ok: false, reason: 'not-found' };
      }

      const [removed] = currentState.items.splice(normalizedIndex, 1);
      setState(currentState, { action: 'remove', item: removed, ...detail });
      return { ok: true, item: removed };
    };

    const clearItems = (detail = {}) => {
      setState({ items: [] }, { action: 'clear', ...detail });
    };

    const syncVariantPrice = (variant, sectionId = '') => {
      if (!variant || !variant.id) return;

      const selector = sectionId
        ? `[data-pdp-bundle-price][data-section-id="${String(sectionId)}"]`
        : '[data-pdp-bundle-price]';
      const priceEls = document.querySelectorAll(selector);
      if (!priceEls.length) return;

      const variantPrice = Number(variant.price || 0);
      const safeVariantPrice = Number.isFinite(variantPrice) && variantPrice >= 0 ? variantPrice : 0;

      priceEls.forEach((priceEl) => {
        priceEl.dataset.price = String(safeVariantPrice);
        priceEl.dataset.comparePrice = String(safeVariantPrice);
      });

      renderProductPrices();
    };

    const bindListeners = () => {
      if (state.listenersBound) return;

      document.addEventListener('click', (event) => {
        const guidanceTrigger = event.target.closest('[data-pdp-bundle-guidance-trigger]');
        if (guidanceTrigger) {
          event.preventDefault();
          document.dispatchEvent(new CustomEvent('fct:pdp-bundle-guidance:show'));
          return;
        }

        const removeButton = event.target.closest('[data-pdp-bundle-remove-index]');
        if (removeButton) {
          const removeIndex = Number(removeButton.dataset.pdpBundleRemoveIndex);
          removeItemAt(removeIndex, {
            source: 'sticky-bar-remove'
          });
          return;
        }

        const cta = event.target.closest('[data-pdp-bundle-cta]');
        if (!cta) return;
        if (!isActive() || cta.disabled) return;

        void addBundleToCart();
      });

      window.addEventListener('popstate', () => {
        syncModeClass();
        renderBars();
        renderProductPrices();
      });

      document.addEventListener('pdp:swap:success', () => {
        syncModeClass();
        renderBars();
        renderProductPrices();
      });

      document.addEventListener('product:variant-change', (event) => {
        if (!event?.detail?.variant) return;
        syncVariantPrice(event.detail.variant, event.detail.sectionId);
      });

      document.addEventListener('DOMContentLoaded', () => {
        syncModeClass();
        renderBars();
        renderProductPrices();
      });

      state.listenersBound = true;
    };

    bindListeners();
    syncModeClass();
    renderProductPrices();

    return {
      modeParam: MODE_PARAM,
      storageKey: STORAGE_KEY,
      isEnabled: isFeatureEnabled,
      isActive,
      getLimit,
      getDiscountCents,
      getSwatchGuidanceBody,
      getState,
      getItems,
      getCountByHandle,
      isFull,
      activate,
      deactivate,
      addItem,
      addBundleToCart,
      removeLastByHandle,
      removeItemAt,
      clearItems,
      renderBars,
      renderProductPrices,
      syncModeClass,
      syncVariantPrice,
      openCartDrawer
    };
  })();
}
